import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { formatMoney } from "../lib/money";
import CountUp from "../components/CountUp";
import { toast } from "sonner";
import {
  Coins, Sparkles, Plus, Clock, TrendingUp, AlertTriangle,
  ShieldCheck, ArrowUpRight, Zap, Award, Mail, RotateCcw, Gift,
} from "lucide-react";

/**
 * Diplomexa Diploks Wallet — single source of truth for an organisation's
 * platform-credit balance, ledger and top-up flow.
 *
 * Compliance: every surface here speaks of "platform credits" — never cash,
 * never a financial wallet, never crypto. The fine-print banner at the
 * bottom carries the legal wording verbatim from the spec.
 *
 * The four credit-type cards reflect the consumption priority:
 *   Trial → Bonus → Subscription → Purchased.
 */

const TYPE_META = {
  trial:        { label: "Trial Diploks",        icon: Sparkles,
                  accent: "text-amber-700 bg-amber-50 border-amber-200",
                  hint: "From your free demo signup." },
  bonus:        { label: "Bonus Diploks",        icon: Award,
                  accent: "text-emerald-700 bg-emerald-50 border-emerald-200",
                  hint: "Earned with top-up promos." },
  subscription: { label: "Subscription Diploks", icon: TrendingUp,
                  accent: "text-blue-700 bg-blue-50 border-blue-200",
                  hint: "Included with your plan." },
  purchased:    { label: "Purchased Diploks",    icon: Zap,
                  accent: "text-[#0A192F] bg-[#D4AF37]/15 border-[#D4AF37]/40",
                  hint: "Bought from a Diploks pack." },
};

const ACTION_LABEL = {
  diploks_used:               "Used",
  diploks_topup_success:      "Top-up · Purchased",
  bonus_diploks_added:        "Top-up · Bonus",
  trial_diploks_added:        "Trial grant",
  subscription_diploks_granted: "Subscription grant",
  admin_adjustment:           "Admin adjustment",
  diploks_reversal:           "Reversed",
  diploks_refunded:           "Refunded",
  diploks_expired:            "Expired",
  diploks_topup_failed:       "Top-up failed",
};

export default function DiploksWalletPage() {
  const [walletData, setWalletData] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [w, l] = await Promise.all([
        api.get("/diploks/wallet"),
        api.get("/diploks/ledger?limit=30"),
      ]);
      setWalletData(w.data);
      setLedger(l.data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const bal = walletData?.balance;
  const totalAvailable = bal?.total_available ?? 0;
  const lowBalance = walletData?.low_balance;
  const expiringSoon = bal?.total_expiring_soon ?? 0;
  const earliestExp = bal?.earliest_expiry;
  // Dedupe eligible campaigns by id — the engine occasionally emits
  // the same founding campaign under multiple eligibility rules.
  const campaigns = useMemo(() => {
    const raw = walletData?.eligible_campaigns || [];
    const seen = new Set();
    return raw.filter((c) => {
      if (!c?.id || seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [walletData]);

  const breakdown = useMemo(() => {
    if (!bal?.breakdown) return [];
    return ["trial", "bonus", "subscription", "purchased"]
      .map((k) => ({ key: k, ...TYPE_META[k], ...bal.breakdown[k] }));
  }, [bal]);

  return (
    <DashboardLayout title="Diploks Wallet">
      <div className="space-y-6" data-testid="diploks-wallet-page">
        {/* Hero — total balance + primary CTA */}
        <div className="relative overflow-hidden rounded-sm bg-brand-navy-dark text-brand-champagne p-7 md:p-9 border border-brand-gold/30 shadow-editorial">
          <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
          <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
          <div aria-hidden className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-brand-gold/15 blur-3xl pointer-events-none" />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative">
            <div>
              <p className="eyebrow text-brand-gold flex items-center gap-1.5">
                <Coins className="h-3 w-3" /> Diplomexa Diploks
              </p>
              <p
                className="mt-3 font-display text-5xl md:text-6xl text-brand-champagne leading-none tabular-nums"
                data-testid="diploks-total-available"
              >
                {loading ? "—" : (
                  <CountUp
                    to={totalAvailable}
                    duration={1.4}
                    milestoneEvery={100}
                    milestoneMin={100}
                  />
                )}
              </p>
              <div className="divider-gold-hairline mt-5 max-w-xs" />
              <p className="mt-4 text-sm text-brand-champagne/70">
                {loading ? "Loading wallet…" : "Diploks available across all types"}
              </p>
              {expiringSoon > 0 && earliestExp && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-amber-300 bg-amber-500/15 border border-amber-400/40 rounded-sm px-2.5 py-1 uppercase tracking-[0.18em] font-semibold">
                  <Clock className="h-3 w-3" />
                  {expiringSoon} Diploks expire within 7 days · earliest {earliestExp?.slice(0, 10)}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <Link
                to="/dashboard/diploks/topup"
                data-testid="diploks-topup-cta"
                className="inline-flex items-center gap-2 bg-brand-gold text-brand-navy-dark font-bold px-6 py-3 rounded-sm uppercase tracking-[0.18em] text-xs shadow-gold-soft hover:shadow-gold-strong hover:bg-brand-gold-light transition-all duration-300"
              >
                <Plus className="h-4 w-4" />
                Top up Diploks
              </Link>
              <button
                type="button"
                onClick={refresh}
                className="text-[10px] uppercase tracking-[0.22em] text-brand-champagne/55 hover:text-brand-gold inline-flex items-center gap-1.5 font-semibold transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Founding Organiser / bonus campaign ribbon */}
        {campaigns.length > 0 && (
          <div className="space-y-2" data-testid="diploks-wallet-campaign-ribbons">
            {campaigns.map((c) => (
              <Link
                key={c.id}
                to="/dashboard/diploks/topup"
                data-testid={`diploks-wallet-ribbon-${c.key}`}
                className="block relative overflow-hidden rounded-xl border border-[#D4AF37]/50 bg-gradient-to-r from-[#0A192F] via-[#0F2C50] to-[#0A192F] text-white px-5 py-3 hover:border-[#D4AF37] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Gift className="h-5 w-5 text-[#D4AF37] shrink-0" />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#D4AF37]">
                      {c.label}
                    </p>
                    <p className="text-sm mt-0.5">{c.ribbon_text}</p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-[#D4AF37]" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Low-balance nudge */}
        {!loading && lowBalance && (
          <div
            data-testid="diploks-low-balance-banner"
            className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                Your balance is running low.
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                You have {totalAvailable} Diploks left. Add Diploks to keep
                inviting delegates and generating premium reports without
                interruption.
              </p>
            </div>
            <Link
              to="/dashboard/diploks/topup"
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-700 text-white hover:bg-amber-800"
            >
              Top up now
            </Link>
          </div>
        )}

        {/* Breakdown cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="diploks-breakdown-grid">
          {breakdown.map(({ key, label, icon: Icon, accent, hint, available, used, expiring_soon }) => (
            <div
              key={key}
              data-testid={`diploks-breakdown-${key}`}
              className={`rounded-xl border p-4 ${accent}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold">{label}</p>
              </div>
              <p className="mt-2 text-2xl font-bold">{available?.toLocaleString() || 0}</p>
              <p className="text-[11px] opacity-75 mt-0.5">{hint}</p>
              {(used > 0 || expiring_soon > 0) && (
                <div className="mt-2 pt-2 border-t border-current/15 flex justify-between text-[10px] opacity-75">
                  <span>Used: {used}</span>
                  {expiring_soon > 0 && <span>Expiring: {expiring_soon}</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Two columns — usage history + quick links */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Ledger */}
          <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white">
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-sm font-bold text-[#0A192F]">Usage history</p>
              <p className="text-[11px] text-slate-500">Latest 30 ledger entries</p>
            </div>
            <div className="divide-y divide-slate-100 max-h-[480px] overflow-auto" data-testid="diploks-ledger-list">
              {loading && (
                <p className="px-5 py-8 text-center text-xs text-slate-500">Loading ledger…</p>
              )}
              {!loading && ledger.length === 0 && (
                <p className="px-5 py-8 text-center text-xs text-slate-500">
                  No Diploks activity yet. Your wallet is fresh!
                </p>
              )}
              {ledger.map((row, idx) => {
                const isCredit = (row.amount_added || 0) > 0;
                const delta = isCredit ? `+${row.amount_added}` : `-${row.amount_used}`;
                // ledger_id may be missing on legacy rows — fall back to a
                // composite that's still stable for this render.
                const k = row.ledger_id || `${row.created_at || ""}-${row.action_type}-${idx}`;
                return (
                  <div key={k} className="px-5 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#0A192F]">
                        {ACTION_LABEL[row.action_type] || row.action_type}
                        {row.feature_key && (
                          <span className="ml-1.5 text-[10px] font-normal text-slate-500">· {row.feature_key}</span>
                        )}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {row.created_at?.slice(0, 19).replace("T", " ")} · {TYPE_META[row.diploks_type]?.label || row.diploks_type}
                      </p>
                      {row.reason && (
                        <p className="text-[10px] text-slate-500 mt-0.5 truncate">{row.reason}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold ${isCredit ? "text-emerald-700" : "text-slate-700"}`}>
                        {delta}
                      </p>
                      <p className="text-[10px] text-slate-400">bal {row.balance_after}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Side — quick actions + compliance */}
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-bold text-[#0A192F]">Quick actions</p>
              <div className="mt-3 space-y-2">
                <Link
                  to="/dashboard/diploks/topup"
                  className="block w-full text-center text-sm font-semibold px-4 py-2.5 rounded-md bg-[#0A192F] text-white hover:bg-[#0F2C50]"
                >
                  Buy Diploks pack
                </Link>
                <Link
                  to="/dashboard/billing"
                  className="block w-full text-center text-sm font-semibold px-4 py-2.5 rounded-md border border-slate-200 hover:bg-slate-50"
                >
                  View billing & invoices
                </Link>
                <Link
                  to="/pricing"
                  className="block w-full text-center text-sm font-semibold px-4 py-2.5 rounded-md border border-slate-200 hover:bg-slate-50"
                >
                  See subscription plans
                </Link>
              </div>
            </div>

            {/* Compliance notice */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11px] text-slate-600 leading-relaxed"
                 data-testid="diploks-compliance-notice">
              <p className="font-bold text-slate-800 flex items-center gap-1.5 mb-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> About Diploks
              </p>
              {walletData?.compliance_notice ||
                "Diploks are Diplomexa platform credits used to unlock eligible digital services. Diploks are not cash, not cryptocurrency, not a stored-value wallet, not transferable, not withdrawable, not resellable, and cannot be converted into money."}
              <p className="mt-2">
                <Link to="/policies/terms" className="text-[#0F4C81] hover:underline">View full terms →</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
